// TelcoTrack — Úřady: potřebnost, katalog DOSS, příprava stavby, schvalování, podání
// Interní podklad ≠ právní pravda. Výroky katalogu jsou podněty k ověření.

export const AUTHORITY_AGENDA_KINDS = ['official', 'network_crossing', 'construction_prep'] as const;
export type AuthorityAgendaKind = (typeof AUTHORITY_AGENDA_KINDS)[number];

export const AUTHORITY_NECESSITY = ['needed', 'not_needed', 'assess'] as const;
export type AuthorityNecessity = (typeof AUTHORITY_NECESSITY)[number];

export const AUTHORITY_RULE_SOURCES = ['catalog', 'template', 'human', 'legacy', 'overlay'] as const;
export type AuthorityRuleSource = (typeof AUTHORITY_RULE_SOURCES)[number];

/** Overlay / practice may only propose assess — never auto-confirm legal necessity. */
export const AUTHORITY_NEVER_AUTO_NEEDED_SUBJECTS = [
  'jes',
  'mo_sem',
  'hzs',
  'archeologicky_ustav',
] as const;
export type AuthorityNeverAutoNeededSubject = (typeof AUTHORITY_NEVER_AUTO_NEEDED_SUBJECTS)[number];

export const AUTHORITY_WAITING_ON_KINDS = ['authority', 'supplier', 'internal'] as const;
export type AuthorityWaitingOnKind = (typeof AUTHORITY_WAITING_ON_KINDS)[number];

export const AUTHORITY_DEADLINE_SOURCES = ['manual', 'message', 'sla'] as const;
export type AuthorityDeadlineSource = (typeof AUTHORITY_DEADLINE_SOURCES)[number];

export const AUTHORITY_OUTCOMES = [
  'consent',
  'consent_with_conditions',
  'refusal',
  'supplement',
] as const;
export type AuthorityOutcome = (typeof AUTHORITY_OUTCOMES)[number];

export const AUTHORITY_BLOCK_MODES = ['hard', 'warning'] as const;
export type AuthorityBlockMode = (typeof AUTHORITY_BLOCK_MODES)[number];

export const AUTHORITY_EVIDENCE_KINDS = [
  'document',
  'contract',
  'oral_agreement',
  'portal',
  'order',
  'email',
] as const;
export type AuthorityEvidenceKind = (typeof AUTHORITY_EVIDENCE_KINDS)[number];

export const AUTHORITY_DECISION_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'returned',
  'rejected',
  'sending',
  'sent',
  'failed',
  'unclear',
] as const;
export type AuthorityDecisionStatus = (typeof AUTHORITY_DECISION_STATUSES)[number];

/** Filing slots R100 / MET / UR — ordinary Drive uploads (not KN parcel prefill). */
export const AUTHORITY_LAND_LIST_PACKAGE_KEYS = [
  'land_list_r100',
  'land_list_met',
  'land_list_ur',
] as const;
export type AuthorityLandListPackageKey = (typeof AUTHORITY_LAND_LIST_PACKAGE_KEYS)[number];

/** Starý jediný slot „Seznam pozemků UR/MET/R100“ — čtení / attach, ne nový katalog. */
export const AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY = 'land_list' as const;

export const AUTHORITY_FILING_PACKAGE_KEYS = [
  'c1_overview',
  'c2_plan',
  'tz',
  'power_of_attorney',
  'cover_or_form',
  ...AUTHORITY_LAND_LIST_PACKAGE_KEYS,
  'subject_specific',
] as const;
export type AuthorityFilingPackageKey = (typeof AUTHORITY_FILING_PACKAGE_KEYS)[number];

export const AUTHORITY_FILING_PACKAGE_KEYS_ACCEPTED = [
  ...AUTHORITY_FILING_PACKAGE_KEYS,
  AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY,
] as const;

export const AUTHORITY_FILING_PACKAGE_LABELS_CS: Record<AuthorityFilingPackageKey, string> = {
  c1_overview: 'C.1 Přehledová situace',
  c2_plan: 'C.2 Polohopis',
  tz: 'Technická zpráva',
  power_of_attorney: 'Plná moc investora',
  cover_or_form: 'Průvodní dopis nebo formulář příjemce',
  land_list_r100: 'R100',
  land_list_met: 'MET',
  land_list_ur: 'UR',
  subject_specific: 'Zvláštní přílohy subjektu',
};

/**
 * Explicitly optional filing slots (UI: „Volitelné“).
 * All other catalog slots are recommended-only (UI: „Doporučeno“) and never block send.
 */
export const AUTHORITY_OPTIONAL_FILING_KEYS: readonly AuthorityFilingPackageKey[] = ['c1_overview'];

export const AUTHORITY_CONSTRUCTION_PREP_KEYS = [
  'dio',
  'aggregates',
  'container_hauler',
  'mobile_wc',
  'handover_to_site_lead',
  'recycling_site',
  'material_storage',
  'stakeout',
] as const;
export type AuthorityConstructionPrepKey = (typeof AUTHORITY_CONSTRUCTION_PREP_KEYS)[number];

export const AUTHORITY_CONSTRUCTION_PREP_LABELS_CS: Record<AuthorityConstructionPrepKey, string> = {
  dio: 'DIO',
  aggregates: 'Dodavatel materiálu',
  container_hauler: 'Dopravce',
  mobile_wc: 'Mobilní WC',
  handover_to_site_lead: 'Předání staveniště',
  recycling_site: 'Skládka',
  material_storage: 'Deponie',
  stakeout: 'Vytyčení',
};

/** Older prep keys still classify as Povinnosti when they already exist on a stavba. */
export const AUTHORITY_CONSTRUCTION_PREP_LEGACY_KEYS = ['renew_statements', 'traffic_signs'] as const;
export type AuthorityConstructionPrepLegacyKey = (typeof AUTHORITY_CONSTRUCTION_PREP_LEGACY_KEYS)[number];

export const AUTHORITY_CONSTRUCTION_PREP_LEGACY_LABELS_CS: Record<AuthorityConstructionPrepLegacyKey, string> = {
  renew_statements: 'Obnovení vyjádření',
  traffic_signs: 'Dopravní značení',
};

export function isAuthorityPovinnostKey(value: string | null | undefined): boolean {
  return (AUTHORITY_CONSTRUCTION_PREP_KEYS as readonly string[]).includes((value ?? '').trim());
}

export function isAuthorityPovinnostLegacyKey(value: string | null | undefined): boolean {
  return (AUTHORITY_CONSTRUCTION_PREP_LEGACY_KEYS as readonly string[]).includes((value ?? '').trim());
}

/** Core eight checklist items — always present, never imported, never deleted. */
export function isCoreAuthorityPovinnost(input: {
  subjectKey?: string | null;
  documentType?: string | null;
  catalogKey?: string | null;
}): boolean {
  if (isAuthorityPovinnostKey(input.subjectKey) || isAuthorityPovinnostKey(input.documentType)) {
    return true;
  }
  const catalog = (input.catalogKey ?? '').trim();
  return (AUTHORITY_CONSTRUCTION_PREP_KEYS as readonly string[]).some((key) => catalog === `prep:${key}`);
}

export function isAuthorityConstructionPrepKey(value: string | null | undefined): boolean {
  const key = (value ?? '').trim();
  return isAuthorityPovinnostKey(key) || isAuthorityPovinnostLegacyKey(key);
}

export const AUTHORITY_WC_BILLING_COMPANIES = ['FiberBeta s.r.o.', 'ProTelco s.r.o.'] as const;

export const AUTHORITY_WC_REQUIRED_FIELDS = [
  'dateFrom',
  'constructionName',
  'billingActionNumber',
  'company',
  'siteContact',
] as const;

export const AUTHORITY_WC_ORDER_STAGES = [
  'draft',
  'approved',
  'sent',
  'supplier_confirmed',
  'agreed_date',
] as const;
export type AuthorityWcOrderStage = (typeof AUTHORITY_WC_ORDER_STAGES)[number];

/** Editable supplier preference — not exclusive. */
export const AUTHORITY_DEFAULT_AGGREGATE_PREFERENCES = ['CEMEX', 'České štěrkopísky'] as const;

export interface AuthorityDossCatalogEntry {
  subjectKey: string;
  labelCs: string;
  whenHintCs: string;
  specialHintCs: string;
  /** Internal practice suggestion — never auto-effective law. */
  defaultNecessity: AuthorityNecessity;
  legalClaim: false;
}

export const AUTHORITY_DOSS_CATALOG: readonly AuthorityDossCatalogEntry[] = [
  {
    subjectKey: 'obec',
    labelCs: 'Obec s dotčenými pozemky',
    whenHintCs: 'Všechny dotčené obce, ne jedna na polygon. Samostatný příjemce a sledování.',
    specialHintCs: '',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'sus',
    labelCs: 'SÚS',
    whenHintCs: 'Pozemek/vozovka v její správě i bez KN vlastnictví.',
    specialHintCs: 'Okótovaná trasa od vozovky; podvrt/překop; u podvrtu jámy a kóty; tabulka zásahu SÚS.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'spu',
    labelCs: 'SPÚ',
    whenHintCs: 'Jeho pozemek.',
    specialHintCs: 'Přílohy neurčeny.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'povodi',
    labelCs: 'Povodí',
    whenHintCs: 'Křížení toku / VD / záplava / OP vodního zdroje.',
    specialHintCs: 'Způsob přechodu; u podvrtu jámy a kóty od břehu; řez křížení.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'lesy_cr',
    labelCs: 'Lesy ČR',
    whenHintCs: 'PUPFL / OP lesa.',
    specialHintCs: 'Zábor; v OP kóta k hranici lesa.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'aopk',
    labelCs: 'AOPK',
    whenHintCs: 'CHÚ, NP, CHKO, ptačí, Natura — použitelnost k potvrzení.',
    specialHintCs: '',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'npu',
    labelCs: 'NPÚ',
    whenHintCs: 'PZ, historické centrum, KP.',
    specialHintCs: 'Podrobnější situace po konzultaci s HP; kaplička/socha = poznámka oslovit PP.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'mo_sem',
    labelCs: 'MO – SEM',
    whenHintCs: 'Interní praxe vždy (vojenské kabely). Výchozí návrh, ne právní tvrzení.',
    specialHintCs: '',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'archeologicky_ustav',
    labelCs: 'Archeologický ústav',
    whenHintCs: 'Interní praxe vždy. Výchozí návrh, ne právní tvrzení.',
    specialHintCs: '',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'uzsvm',
    labelCs: 'ÚZSVM',
    whenHintCs: 'Jeho pozemek.',
    specialHintCs: 'Přílohy neurčeny.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'rsd',
    labelCs: 'ŘSD',
    whenHintCs: 'Jeho správa; I. třída, dálnice.',
    specialHintCs: 'Podélný/příčný řez podvrtu; kóty; způsob přechodu; jámy.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'jes',
    labelCs: 'JES',
    whenHintCs: 'Podklad: vždy na SÚ; při přírodním prvku předem Povodí/Lesy/AOPK. Rozsah potvrdí člověk.',
    specialHintCs: 'Přírodní podklady (Povodí/Lesy/AOPK) řeš zvlášť dle potřeby. Ne automaticky účinné právní pravidlo.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'koordinovane_stanovisko',
    labelCs: 'Závazné koordinované stanovisko',
    whenHintCs: 'Náhrada JES v některých městech; stejné přírodní podklady. Varianta k posouzení.',
    specialHintCs: 'Ne auto dvě žádosti ani auto rušení.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'hzs',
    labelCs: 'HZS',
    whenHintCs: 'FTTH šablona obsahuje položku; existence ≠ povinnost pro každou stavbu.',
    specialHintCs: 'Výjimečně na výzvu. Nevymýšlej pravidla.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'khs',
    labelCs: 'KHS',
    whenHintCs: 'Výjimečně (výzva).',
    specialHintCs: 'Nevymýšlej pravidla.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'pcr',
    labelCs: 'PČR',
    whenHintCs: 'Výjimečně (výzva).',
    specialHintCs: 'Nevymýšlej pravidla.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'drazni_urad',
    labelCs: 'Drážní úřad',
    whenHintCs: 'Křížení / blízkost železnice (overlay). Jinak výjimečně na výzvu.',
    specialHintCs: 'Nevymýšlej pravidla mimo zjištěný zásah.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
  {
    subjectKey: 'cgs_chlu',
    labelCs: 'CHLÚ / OBU (ČGS)',
    whenHintCs: 'Průnik s chráněným ložiskovým územím — podnět k OBU / báňskému úřadu.',
    specialHintCs: 'Jen overlay; ne slepý katalog. Potvrzení člověkem.',
    defaultNecessity: 'assess',
    legalClaim: false,
  },
] as const;

export const AUTHORITY_WORKFLOW_GATES = ['BUILD_READY'] as const;
export type AuthorityWorkflowGate = (typeof AUTHORITY_WORKFLOW_GATES)[number];

export interface AuthorityApprovalPolicy {
  requireApprovalForOutbound: boolean;
  allowSelfApprove: boolean;
  historicSendsAreHistory: true;
}

export const AUTHORITY_DEFAULT_APPROVAL_POLICY: AuthorityApprovalPolicy = {
  requireApprovalForOutbound: true,
  allowSelfApprove: false,
  historicSendsAreHistory: true,
};

export function isAuthorityNecessity(value: string | null | undefined): value is AuthorityNecessity {
  return !!value && (AUTHORITY_NECESSITY as readonly string[]).includes(value);
}

export function isAuthorityAgendaKind(value: string | null | undefined): value is AuthorityAgendaKind {
  return !!value && (AUTHORITY_AGENDA_KINDS as readonly string[]).includes(value);
}

export function isAuthorityDecisionStatus(value: string | null | undefined): value is AuthorityDecisionStatus {
  return !!value && (AUTHORITY_DECISION_STATUSES as readonly string[]).includes(value);
}

export function isAuthorityFilingPackageKey(
  value: string | null | undefined,
): value is AuthorityFilingPackageKey | typeof AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY {
  return !!value && (AUTHORITY_FILING_PACKAGE_KEYS_ACCEPTED as readonly string[]).includes(value);
}

export function isLandListFilingPackageKey(value: string | null | undefined): boolean {
  const key = (value ?? '').trim();
  return (
    key === AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY ||
    (AUTHORITY_LAND_LIST_PACKAGE_KEYS as readonly string[]).includes(key)
  );
}

/** Legacy `land_list` files and slots bind to Seznam UR. */
export function canonicalLandListPackageKey(
  value: string | null | undefined,
): AuthorityLandListPackageKey | null {
  const key = (value ?? '').trim();
  if (key === AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY) return 'land_list_ur';
  if ((AUTHORITY_LAND_LIST_PACKAGE_KEYS as readonly string[]).includes(key)) {
    return key as AuthorityLandListPackageKey;
  }
  return null;
}

export function filingMatchesPackageKey(storedKey: string | null | undefined, catalogKey: string): boolean {
  const stored = (storedKey ?? '').trim();
  const catalog = catalogKey.trim();
  if (!stored || !catalog) return false;
  if (stored === catalog) return true;
  return catalog === 'land_list_ur' && stored === AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY;
}

/** Expand the old combined land_list row into R100 / MET / UR without duplicating split keys. */
export function expandLandListFilingItems<T extends { packageKey: string }>(items: readonly T[]): T[] {
  const hasSplit = AUTHORITY_LAND_LIST_PACKAGE_KEYS.some((key) =>
    items.some((item) => item.packageKey === key),
  );
  const out: T[] = [];
  let injected = false;
  for (const item of items) {
    if (item.packageKey !== AUTHORITY_LEGACY_LAND_LIST_PACKAGE_KEY) {
      out.push(item);
      continue;
    }
    if (hasSplit) continue;
    for (const packageKey of AUTHORITY_LAND_LIST_PACKAGE_KEYS) {
      out.push({ ...item, packageKey });
    }
    injected = true;
  }
  return injected || hasSplit ? out : [...items];
}

/** Unknown territorial data must stay assess — never auto not_needed. */
export function necessityFromUnknownTerritory(): AuthorityNecessity {
  return 'assess';
}

export function isNeverAutoNeededSubject(subjectKey: string | null | undefined): boolean {
  return (AUTHORITY_NEVER_AUTO_NEEDED_SUBJECTS as readonly string[]).includes((subjectKey ?? '').trim());
}

/** Overlay and practice proposals are always assess — never auto needed. */
export function overlayProposalNecessity(subjectKey?: string | null): AuthorityNecessity {
  void subjectKey;
  return 'assess';
}

export function isManualNecessityLock(necessity: string | null | undefined): boolean {
  return necessity === 'needed' || necessity === 'not_needed';
}

/** Hard-block BUILD_READY only for confirmed `needed` povinnosti. Categories 1–2 never gate. */
export function obligationBlocksReadiness(input: {
  necessity: string | null | undefined;
  status: string | null | undefined;
  exceptionReason?: string | null;
  validUntil?: string | null;
  todayYmd?: string | null;
  agendaKind?: string | null;
}): boolean {
  const agenda = (input.agendaKind ?? '').trim();
  if (agenda === 'official' || agenda === 'network_crossing') return false;
  if ((input.exceptionReason ?? '').trim()) return false;
  if (input.necessity === 'not_needed') return false;
  if (input.necessity === 'assess' || !input.necessity) return false;
  if (input.status === 'n_a' || input.status === 'received' || input.status === 'approved') {
    const until = (input.validUntil ?? '').trim();
    const today = (input.todayYmd ?? '').trim();
    if (until && today && until < today) return true;
    return input.status !== 'received' && input.status !== 'approved';
  }
  return true;
}

/** `assess` / „nutno posoudit“ — viditelné varování, ne tvrdá blokace BUILD_READY. */
export function obligationWarnsReadiness(input: {
  necessity: string | null | undefined;
  exceptionReason?: string | null;
}): boolean {
  if ((input.exceptionReason ?? '').trim()) return false;
  return input.necessity === 'assess' || !input.necessity;
}

/**
 * Filing package slots (C.2, TZ, plná moc, R100/MET/UR, …) are recommendations only.
 * Missing přílohy must never block Datovka/Email send.
 */
export function filingItemBlocksSend(
  _key: string,
  _present: boolean,
  _requiredOverride?: boolean,
): boolean {
  return false;
}

export function normalizeMunicipalityKey(name: string): string {
  return name
    .trim()
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function catalogKeyForDoss(subjectKey: string, municipality?: string | null): string {
  const mun = municipality?.trim() ? normalizeMunicipalityKey(municipality) : '';
  return mun ? `doss:${subjectKey}:${mun}` : `doss:${subjectKey}`;
}

export function catalogKeyForPrep(area: AuthorityConstructionPrepKey): string {
  return `prep:${area}`;
}

export function wouldCreateDependencyCycle(
  edges: ReadonlyArray<{ fromId: string; toId: string }>,
  fromId: string,
  toId: string,
): boolean {
  if (fromId === toId) return true;
  const outgoing = new Map<string, string[]>();
  for (const edge of edges) {
    const list = outgoing.get(edge.fromId) ?? [];
    list.push(edge.toId);
    outgoing.set(edge.fromId, list);
  }
  const next = outgoing.get(fromId) ?? [];
  if (!next.includes(toId)) {
    outgoing.set(fromId, [...next, toId]);
  }
  const seen = new Set<string>();
  const stack = [toId];
  while (stack.length) {
    const node = stack.pop() as string;
    if (node === fromId) return true;
    if (seen.has(node)) continue;
    seen.add(node);
    for (const child of outgoing.get(node) ?? []) stack.push(child);
  }
  return false;
}

export function supplierOfferSortKey(offer: {
  price?: number | null;
  transportPrice?: number | null;
}): number | null {
  if (offer.price == null || !Number.isFinite(offer.price)) return null;
  const transport = offer.transportPrice;
  if (transport == null || !Number.isFinite(transport)) return null;
  return offer.price + transport;
}

export function cheapestCompleteOfferIndex(
  offers: ReadonlyArray<{ price?: number | null; transportPrice?: number | null }>,
): number | null {
  let bestIndex: number | null = null;
  let bestTotal = Number.POSITIVE_INFINITY;
  for (let index = 0; index < offers.length; index += 1) {
    const total = supplierOfferSortKey(offers[index]);
    if (total == null) continue;
    if (total < bestTotal) {
      bestTotal = total;
      bestIndex = index;
    }
  }
  return bestIndex;
}

export function missingWcOrderFields(payload: Record<string, unknown> | null | undefined): string[] {
  const rec = payload ?? {};
  return AUTHORITY_WC_REQUIRED_FIELDS.filter((field) => {
    const value = rec[field];
    return value == null || String(value).trim() === '';
  });
}

export function approvalFingerprint(input: {
  recipient: string;
  subject: string;
  bodyText?: string | null;
  attachmentIds?: string[];
  mailboxId?: string | null;
}): string {
  const attachments = [...(input.attachmentIds ?? [])].map((id) => id.trim()).filter(Boolean).sort();
  return JSON.stringify({
    recipient: input.recipient.trim(),
    subject: input.subject.trim(),
    bodyText: (input.bodyText ?? '').trim(),
    attachments,
    mailboxId: (input.mailboxId ?? '').trim(),
  });
}

export function approvalInvalidatedByChange(
  approvedFingerprint: string | null | undefined,
  next: Parameters<typeof approvalFingerprint>[0],
): boolean {
  if (!approvedFingerprint) return true;
  return approvedFingerprint !== approvalFingerprint(next);
}
